import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Bars3Icon } from '@heroicons/react/24/outline';
import Sidebar from './Sidebar';
import { cn } from '../../lib/utils';

const MainLayout = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // Track viewport size so we know when to treat the sidebar as an overlay
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);

      // On small screens keep the sidebar closed by default when viewport changes.
      // Use functional update to avoid coupling this effect to isCollapsed changes.
      if (mobile) {
        setIsCollapsed((prev) => (prev ? prev : true));
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsCollapsed((prev) => !prev);
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      {/* Two-column layout: sidebar + main content */}
      <div
        className={cn('grid min-h-screen transition-all duration-300 ease-in-out', {
          'grid-cols-1': isMobile,
        })}
        style={!isMobile ? { gridTemplateColumns: isCollapsed ? '4rem auto' : '14rem auto' } : {}}
      >
        {/* Sidebar (sticky on desktop, overlay on mobile) */}
        {!isMobile && (
          <div className="relative">
            <div className="sticky top-0 h-screen">
              <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
            </div>
          </div>
        )}
        {isMobile && <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />}

        {/* Main content area — min-w-0 so grid column can shrink and not cause page scroll */}
        <div className="flex flex-col h-screen min-w-0 bg-[#F7F9FC] overflow-hidden">
          <main
            id="main-content"
            className="flex-1 w-full min-w-0 overflow-y-auto overflow-x-hidden"
            style={{
              padding: 'clamp(0.75rem, 1.5vw, 1.5rem)',
            }}
          >
            <div className="w-full min-w-0 overflow-x-hidden">
              {/* Mobile sidebar toggle */}
              {isMobile && (
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={toggleSidebar}
                    className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 hover:text-gray-900"
                    aria-label="Toggle navigation"
                  >
                    <Bars3Icon className="h-4 w-4" />
                    <span>Menu</span>
                  </button>
                </div>
              )}

              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default MainLayout;
